import { ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatAmount } from '@/lib/utils'
import { DEFAULT_CATEGORY_COLOR } from '@/lib/constants'
import type { CategoryDeviation, DeviationLevel } from '@/lib/statistics'

type Props = {
  deviations: CategoryDeviation[]
  getColor: (parentName: string) => string
  onSelectCategory: (parentName: string) => void
  /**
   * Budgeted amount per category. When provided, the bar scale also takes the
   * budget into account so a row whose current spend equals its budget reads
   * as "full", and a tick mark is drawn at the budget position.
   */
  budgetByCategory?: Map<string, number>
}

const LEVEL_LABELS: Record<DeviationLevel, string> = {
  'very-high': '⚠ かなり多い',
  high: '↑ やや多い',
  normal: '通常',
  low: '↓ 少なめ',
}

const LEVEL_TONE: Record<DeviationLevel, string> = {
  'very-high': 'text-destructive',
  high: 'text-amber-600 dark:text-amber-500',
  normal: 'text-muted-foreground',
  low: 'text-emerald-600 dark:text-emerald-500',
}

export function CategoryDeviationCard({
  deviations,
  getColor,
  onSelectCategory,
  budgetByCategory,
}: Props) {
  if (deviations.length === 0) return null

  const visibleRows = deviations.filter((d) => d.current > 0 || d.baselineMean > 0)
  if (visibleRows.length === 0) return null

  // If we have no historical baseline at all, hide the comparative framing
  // and present a plain ranked list — claiming "通常通り" with zero history is
  // misleading.
  const hasAnyBaseline = visibleRows.some((d) => d.baselineMean > 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">カテゴリ別の偏差</CardTitle>
        <p className="text-xs text-muted-foreground">
          {hasAnyBaseline ? '過去6ヶ月の平均と今月を比較' : '今月のカテゴリ別支出'}
        </p>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {visibleRows.map((d) => {
            const color = getColor(d.parentName) ?? DEFAULT_CATEGORY_COLOR
            const budget = budgetByCategory?.get(d.parentName)
            const overBudget = budget !== undefined && budget > 0 && d.current > budget
            const hasBaseline = d.baselineMean > 0

            // Per-row scale: max(current, baseline, budget). When current equals
            // budget the bar reads as full; when current overshoots, the budget
            // tick stays visible inside the bar to show the breach.
            const maxScale = Math.max(d.current, d.baselineMean, budget ?? 0, 1)
            const currentPct = (d.current / maxScale) * 100
            const baselinePct = (d.baselineMean / maxScale) * 100
            const budgetPct =
              budget !== undefined && budget > 0 ? Math.min((budget / maxScale) * 100, 100) : null

            return (
              <li key={d.parentName}>
                <button
                  type="button"
                  onClick={() => onSelectCategory(d.parentName)}
                  className="flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: color }}
                        aria-hidden
                      />
                      <span className="truncate text-sm font-medium">{d.parentName}</span>
                      {overBudget && (
                        <span className="shrink-0 rounded-sm bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                          予算超過
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-sm tabular-nums">
                      <span className="font-medium">{formatAmount(d.current)}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                    {hasBaseline && (
                      <div
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          width: `${baselinePct}%`,
                          backgroundColor: color,
                          opacity: 0.25,
                        }}
                      />
                    )}
                    <div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        width: `${currentPct}%`,
                        backgroundColor: color,
                        opacity: 0.85,
                      }}
                    />
                    {budgetPct !== null && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 w-px bg-foreground/70"
                        style={{ left: `${budgetPct}%` }}
                      />
                    )}
                  </div>

                  {d.classification === 'mostly-fixed' ? (
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                      <span>
                        平均 {formatAmount(d.baselineMean, 'JPY', true)}
                        {budget !== undefined && budget > 0 && (
                          <> ・ 予算 {formatAmount(budget, 'JPY', true)}</>
                        )}
                      </span>
                      <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        固定費
                      </span>
                    </div>
                  ) : hasBaseline ? (
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                      <span>
                        平均 {formatAmount(d.baselineMean, 'JPY', true)}
                        {budget !== undefined && budget > 0 && (
                          <> ・ 予算 {formatAmount(budget, 'JPY', true)}</>
                        )}
                      </span>
                      <span className={cn(LEVEL_TONE[d.level])}>
                        {d.delta >= 0 ? '+' : ''}
                        {formatAmount(d.delta, 'JPY', true)}
                        {d.deltaPct !== null && (
                          <>
                            {' '}
                            ({d.deltaPct >= 0 ? '+' : ''}
                            {Math.round(d.deltaPct * 100)}%)
                          </>
                        )}
                        <span className="ml-1.5">{LEVEL_LABELS[d.level]}</span>
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>
                        {budget !== undefined && budget > 0
                          ? `予算 ${formatAmount(budget, 'JPY', true)}`
                          : '過去データなし'}
                      </span>
                    </div>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
